<script setup>
import {useBaseStore} from "~/stores/base";
import PopupModelInfo from "~/components/product/PopupModelInfo.vue";

const current = ref(0)

const emits = defineEmits(["change"])

/** props **/
const props = defineProps({
  product: {
    default: {},
    required: true
  },
  sku: {
    default: "",
    required: true
  }
})

const isShowPopupModelInfo = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const currentColor = computed(() => props.product.newVariantOptions?.colorList[current.value].color)
const currentModels = computed(() => props.product.optionConfig?.find(config => config.name === currentColor.value).models)
const currentSkuItem = computed(() => props.product.optionConfig?.find(config => config.name === currentColor.value).sizeList.find(item => item.sku === props.sku))

const handleSelect = (index) => {
  current.value = index
  emits("change", index)
}

const handleModelInfoClick = () => {
  isShowPopupModelInfo.value = true
}

const handlePremium = () => {
  useBaseStore().updateIsShowModalPremiumIntroduce(true)
}

</script>

<template>
  <div class="module-product-info-mobile" v-if="isMobile">
    <div class="price">
      Retail Price
      <div class="amount">${{ currentSkuItem?.price || product.price }}</div>
      <div class="premium" v-if="product.premium" @click="handlePremium">
        <img src="@/assets/img/icon-diamond.svg" alt="diamond">
        Premium Style (${{product.premiumFee}})
        <img src="@/assets/img/icon-question-gold.svg" alt="question mark">
      </div>
    </div>
    <div class="colors">
      <div class="selections">
        <div
          class="selection"
          :class="{selected: current === index}"
          v-for="(item, index) in product.newVariantOptions?.colorList"
          @click="handleSelect(index)"
        >
          <div class="color" :style="{backgroundColor: item.colorData}">
            <img v-if="item.colorData.includes('http')" :src="item.colorData" alt="">
          </div>
        </div>
      </div>
      <div class="selected">
        Color: <span class="bold">{{ product.newVariantOptions?.colorList[current].color }}</span>
      </div>
    </div>
    <div class="notes">
      <span class="bold" v-if="product.fitValue">
        <span class="title regular">
          Fit Note: &nbsp;
        </span>
        {{ product.fitValue }} &nbsp;
      </span>
      <span class="models" v-if="currentModels?.length">
        (
          <span class="regular"
                v-for="(model, index) in currentModels">
            Model {{ model.modelName }} is wearing {{ model.wearingSize }}
            <span
              v-if="currentModels?.length > 1 && index !== currentModels?.length - 1">
              ;
            </span>
          </span>
        ) &nbsp;<div class="model-info-container"><span class="model-info bold"
                                                        @click="handleModelInfoClick">Model Info</span><popup-model-info :open="isShowPopupModelInfo" :product="product" :current="current"
        @close="isShowPopupModelInfo = false"/></div>
      </span>
    </div>
  </div>
  <div class="module-product-info-desktop" v-else>
    <div class="price">
      Retail Price
      <div class="amount">${{ currentSkuItem?.price || product.price }}</div>
      <div class="premium" v-if="product.premium" @click="handlePremium">
        <img src="@/assets/img/icon-diamond.svg" alt="diamond">
        Premium Style (${{product.premiumFee}})
        <img src="@/assets/img/icon-question-gold.svg" alt="question mark">
      </div>
    </div>
    <div class="colors">
      <div class="selections">
        <div
          class="selection"
          :class="{selected: current === index}"
          v-for="(item, index) in product.newVariantOptions?.colorList"
          @click="handleSelect(index)"
        >
          <div class="color" :style="{backgroundColor: item.colorData}">
            <img v-if="item.colorData.includes('http')" :src="item.colorData" alt="">
          </div>
        </div>
      </div>
      <div class="selected">
        Color: <span class="bold">{{ product.newVariantOptions?.colorList[current].color }}</span>
      </div>
    </div>
    <div class="notes">
      <span class="bold" v-if="product.fitValue">
        <span class="title regular">
          Fit Note: &nbsp;
        </span>
        {{ product.fitValue }} &nbsp;
      </span>
      <span class="models" v-if="currentModels?.length">
        (
          <span class="regular"
                v-for="(model, index) in currentModels">
            Model {{ model.modelName }} is wearing {{ model.wearingSize }}
            <span
              v-if="currentModels?.length > 1 && index !== currentModels?.length - 1">
              ;
            </span>
          </span>
        ) &nbsp;<div class="model-info-container"><span class="model-info bold"
                                                        @click="handleModelInfoClick">Model Info</span><popup-model-info :open="isShowPopupModelInfo" :product="product" :current="current"
        @close="isShowPopupModelInfo = false"/></div>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-product-info-mobile {
  margin-top: 16px;
  padding: 0 16px;

  .price {
    display: flex;
    align-items: center;

    /* Text sm/Regular */
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 20px; /* 142.857% */

    .amount {
      /* Text sm/Bold */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 20px; /* 142.857% */
      margin-left: 10px;
    }

    .premium {
      display: flex;
      align-items: center;
      gap: 3px;
      margin-left: 10px;

      color: $color-BNTO-beige-dark;
      /* Text sm/Bold */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 650;
      line-height: 20px; /* 142.857% */

      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }

      img {
        width: 18px;
        height: 18px;
      }
    }
  }

  .colors {
    display: flex;
    align-items: center;
    margin-top: 12px;

    .selections {
      display: flex;
      margin-right: 6px;

      .selection {
        border: 0.5px $color-BNTO-beige-dark solid;

        display: flex;
        justify-content: center;
        align-items: center;

        width: 28px;
        height: 28px;
        border-radius: 50%;
        margin-right: 10px;

        cursor: pointer;

        &:last-child {
          margin-right: 0;
        }

        .color {
          width: 23px;
          height: 23px;
          border-radius: 50%;

          img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
          }
        }
      }

      .selected {
        border: 1.5px $color-gray-800 solid;
      }
    }

    .selected {
      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px; /* 142.857% */
      color: $color-gray-700;

      .bold {
        margin-left: 6px;
        /* Text sm/Bold */
        font-family: "TWK Lausanne";
        font-weight: 650;
        font-size: 14px;
        font-style: normal;
        line-height: 20px; /* 142.857% */
        color: $color-gray-800;
      }
    }
  }

  .notes {
    margin-top: 12px;
    /* Text sm/Regular */
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 20px; /* 142.857% */

    .title {
      color: $color-gray-600;
    }

    .bold {
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 20px; /* 142.857% */
    }

    .regular {
      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px;
    }

    .models {
      .model-info-container {
        display: inline-block;

        .model-info {
          text-decoration-line: underline;
          cursor: pointer;
        }
      }
    }
  }
}

.module-product-info-desktop {
  margin-top: 24px;

  .price {
    display: flex;
    align-items: center;

    /* Text sm/Regular */
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 20px; /* 142.857% */

    .amount {
      /* Text sm/Bold */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 20px; /* 142.857% */
      margin-left: 10px;
    }

    .premium {
      display: flex;
      align-items: center;
      gap: 3px;
      margin-left: 10px;

      color: $color-BNTO-beige-dark;
      /* Text sm/Bold */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 650;
      line-height: 20px; /* 142.857% */

      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }

      img {
        width: 18px;
        height: 18px;
      }
    }
  }

  .colors {
    display: flex;
    align-items: center;
    margin-top: 12px;

    .selections {
      display: flex;
      margin-right: 6px;

      .selection {
        border: 0.5px $color-BNTO-beige-dark solid;

        display: flex;
        justify-content: center;
        align-items: center;

        width: 28px;
        height: 28px;
        border-radius: 50%;
        margin-right: 10px;

        cursor: pointer;

        &:last-child {
          margin-right: 0;
        }

        .color {
          width: 23px;
          height: 23px;
          border-radius: 50%;

          img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
          }
        }
      }

      .selected {
        border: 1.5px $color-gray-800 solid;
      }
    }

    .selected {
      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px; /* 142.857% */
      color: $color-gray-700;

      .bold {
        /* Text sm/Bold */
        font-family: "TWK Lausanne";
        font-weight: 650;
        font-size: 14px;
        font-style: normal;
        line-height: 20px; /* 142.857% */
        color: $color-gray-800;

        margin-left: 6px;
      }
    }
  }

  .notes {
    margin-top: 12px;
    /* Text sm/Regular */
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 20px; /* 142.857% */

    .title {
      color: $color-gray-600;
    }

    .bold {
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 20px; /* 142.857% */
    }

    .regular {
      /* Text sm/Regular */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 20px;
    }

    .models {
      .model-info-container {
        display: inline-block;
        position: relative;

        .model-info {
          text-decoration-line: underline;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
